import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import MobileNavbar from "../layout/MobileNavbar";
import MobileJobOrderDetail from "./MobileJobOrderDetail";
import MobileChatPanel from "./MobileChatPanel";
import StatusActionMenu from "../StatusActionMenu";
import CallStatusActionMenu from "../CallStatusActionMenu";
import { QuickCompleteDialog, QuickCancelDialog } from "../modals/QuickNIStatusDialogs";
import { QuickCallStatusDialog, NI_CALL_OPTIONS } from "../modals/QuickCallStatusDialog";
import RescheduleModal from "../modals/RescheduleModal";
import useChatNotifications from "../../hooks/useChatNotifications";
import { getLocalToday, calculatePendingAgeing, getAgeingColor, formatDate } from "../../utils/helpers";
import { getCallStatusColor } from "../../utils/callStatusOptions";
import { MODULE_THEMES } from "../../utils/moduleTheme";

const NI = MODULE_THEMES.ni;

const STATUS_BADGE = {
  Pending: "bg-yellow-100 text-yellow-800",
  Completed: "bg-green-100 text-green-800",
  Cancelled: "bg-red-100 text-red-800",
  Rescheduled: "bg-blue-100 text-blue-800",
};

function StatusBadge({ status, jobOrder }) {
  const ageing = jobOrder ? calculatePendingAgeing(jobOrder, getLocalToday()) : null;
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[status] || "bg-gray-100 text-gray-700"}`}>
      {status || "—"}
      {ageing !== null && <sup className={`text-[0.65em] ml-0.5 font-bold ${getAgeingColor(ageing)}`}>{ageing}</sup>}
    </span>
  );
}

export default function MobileDashboard({ headerExtra = null, initialDate = null, onDateChange = null, onCustomerLocationClick = null, onNapLocate = null }) {
  const { canViewDetail, hasPerm } = useAuth();
  const canViewChat = hasPerm("chat.view");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ Pending: 0, "Active/Complete": 0, Rescheduled: 0, Cancelled: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(() => {
    const d = initialDate || getLocalToday();
    return { from: d, to: d };
  });

  // Notify parent when date changes
  const setDateAndNotify = useCallback((newFilter) => {
    setDateFilter(newFilter);
    if (onDateChange) onDateChange(newFilter.from);
  }, [onDateChange]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // { id, isHistory }
  const [chatJobOrderId, setChatJobOrderId] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const isLoadMore = useRef(false);
  const abortRef = useRef(null);

  // Quick status-action menu state
  const [statusMenu, setStatusMenu] = useState(null);     // { jo, rect }
  const [directAction, setDirectAction] = useState(null);  // { type, jo }

  const statusMenuActions = useMemo(() => {
    const a = [];
    if (hasPerm("jo.complete")) a.push("complete");
    if (hasPerm("jo.cancel")) a.push("cancel");
    if (hasPerm("jo.reschedule")) a.push("reschedule");
    return a;
  }, [hasPerm]);

  // Quick call-status action state
  const [csMenu, setCsMenu] = useState(null);            // { jo, rect }
  const [csAction, setCsAction] = useState(null);         // { jo, selectedStatus }
  const canEditCallStatus = hasPerm("field.edit.call_status");

  // Real-time chat notifications via SSE
  const chatNotifRef = useRef(null);
  useChatNotifications(canViewChat, useCallback((ev) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === ev.job_order_id && !item.is_history
          ? { ...item, chat_count: ev.chat_count, has_unread_chat: true, unread_count: (item.unread_count || 0) + 1 }
          : item
      )
    );
    chatNotifRef.current = ev;
  }, []));

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset on filter changes
  useEffect(() => {
    if (!isLoadMore.current) {
      setPage(1);
      setItems([]);
    }
  }, [debouncedSearch, dateFilter, statusFilter]);

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request to prevent stale responses from overwriting fresh data
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (isLoadMore.current) setLoadingMore(true);
    else setLoading(true);

    try {
      const params = { page, page_size: 30, sort_by: "job_date", sort_dir: "desc" };
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFilter?.from) params.job_date_from = dateFilter.from;
      if (dateFilter?.to) params.job_date_to = dateFilter.to;
      if (statusFilter) params.jo_status = statusFilter;
      const { data } = await api.get("/job-orders", { params, signal: controller.signal });

      if (isLoadMore.current) {
        setItems((prev) => {
          const seen = new Set(prev.map(r => r.is_history ? `h-${r.history_id}` : `a-${r.id}`));
          const fresh = data.items.filter(r => !seen.has(r.is_history ? `h-${r.history_id}` : `a-${r.id}`));
          return [...prev, ...fresh];
        });
      } else {
        setItems(data.items);
      }
      setTotal(data.total);
      setStatusCounts({
        Pending: data.status_counts?.Pending || 0,
        "Active/Complete": data.status_counts?.["Active/Complete"] || 0,
        Rescheduled: data.status_counts?.Rescheduled || 0,
        Cancelled: data.status_counts?.Cancelled || 0,
      });
    } catch (err) {
      // Ignore aborted requests — a newer request has already taken over
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      if (!isLoadMore.current) {
        setItems([]);
        setTotal(0);
        setStatusCounts({ Pending: 0, "Active/Complete": 0, Rescheduled: 0, Cancelled: 0 });
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadMore.current = false;
    }
  }, [page, debouncedSearch, dateFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasMore = page < Math.max(1, Math.ceil(total / 30));

  useEffect(() => {
    if (loadingMore || !hasMore) return;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadMore.current) {
          isLoadMore.current = true;
          setPage((p) => p + 1);
        }
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadingMore, hasMore]);

  const handleRefresh = () => {
    isLoadMore.current = false;
    setPage(1);
    setItems([]);
    fetchData();
  };

  const handleStatusFilter = (filter) => {
    isLoadMore.current = false;
    setStatusFilter((prev) => prev === filter ? null : filter);
  };

  if (chatJobOrderId !== null) {
    return (
      <MobileChatPanel
        jobOrderId={chatJobOrderId}
        onClose={() => setChatJobOrderId(null)}
        onCountUpdate={(joId, count) => {
          setItems((prev) =>
            prev.map((item) =>
              item.id === joId && !item.is_history
                ? { ...item, chat_count: count, has_unread_chat: false, unread_count: 0 }
                : item
            )
          );
        }}
        notifRef={chatNotifRef}
      />
    );
  }

  if (selectedItem !== null) {
    return (
      <MobileJobOrderDetail
        jobOrderId={selectedItem.id}
        historyMode={selectedItem.isHistory}
        onBack={() => setSelectedItem(null)}
        onRefresh={handleRefresh}
        onNapLocate={onNapLocate}
        notifRef={chatNotifRef}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <MobileNavbar />
      {/* Search + optional toggle — fixed at top */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2 flex items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => { isLoadMore.current = false; setSearch(e.target.value); }}
          placeholder="Search JO#, name, account..."
          className="flex-1 min-w-0 border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
        {headerExtra}
      </div>

      {/* Scrollable content */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain"
      >
        {/* NI badge + Date — scrolls with content */}
        <div className="px-3 pb-2">
          <div className="flex gap-1 items-center">
            <span className={`flex-shrink-0 ${NI.badgeMobile} text-[9px] font-bold uppercase tracking-tight px-1.5 py-1.5 rounded-lg shadow-sm leading-tight whitespace-nowrap`}>New Installation</span>
            <button
              type="button"
              onClick={() => {
                const d = new Date(dateFilter.from + "T00:00:00");
                d.setDate(d.getDate() - 1);
                const prev = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                isLoadMore.current = false;
                setDateAndNotify({ from: prev, to: prev });
              }}
              className={`flex-shrink-0 text-xs text-white ${NI.btnPrimaryStatic} w-7 h-7 rounded font-bold flex items-center justify-center`}
              title="Previous day"
            >&lt;</button>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => { isLoadMore.current = false; setDateAndNotify({ from: e.target.value, to: e.target.value }); }}
              className="flex-1 border rounded-lg px-1 py-1 text-[11px] bg-white min-w-0 overflow-hidden"
            />
            <button
              type="button"
              onClick={() => {
                const d = new Date(dateFilter.from + "T00:00:00");
                d.setDate(d.getDate() + 1);
                const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                isLoadMore.current = false;
                setDateAndNotify({ from: next, to: next });
              }}
              className={`flex-shrink-0 text-xs text-white ${NI.btnPrimaryStatic} w-7 h-7 rounded font-bold flex items-center justify-center`}
              title="Next day"
            >&gt;</button>
          </div>
        </div>

        {/* Status cards */}
        <div className="grid grid-cols-4 gap-1.5 px-3 pb-2">
          {[
            { label: "Pending", key: "Pending", filter: "Pending", cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
            { label: "Done", key: "Active/Complete", filter: "Active/Complete", cls: "bg-green-50 text-green-800 border-green-200" },
            { label: "Resched", key: "Rescheduled", filter: "Rescheduled", cls: "bg-blue-50 text-blue-800 border-blue-200" },
            { label: "Cancel", key: "Cancelled", filter: "Cancelled", cls: "bg-red-50 text-red-800 border-red-200" },
          ].map(({ label, key, filter, cls }) => (
            <button
              key={key}
              onClick={() => handleStatusFilter(filter)}
              className={`rounded-lg px-1 py-2 text-center border text-xs font-medium active:scale-95 transition-transform ${cls} ${
                statusFilter === filter ? "ring-2 ring-blue-500 shadow" : ""
              }`}
            >
              <p className="text-[10px] uppercase tracking-wide">{label}</p>
              <p className="text-base font-bold">{statusCounts[key] || 0}</p>
            </button>
          ))}
        </div>

        {/* Results count */}
        <div className="px-4 pb-2">
          <p className="text-xs text-gray-500">{total} job order{total !== 1 ? "s" : ""}</p>
        </div>

        {/* Job Order Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-20" />
        ) : (
          <div className="px-3 pb-4 space-y-2">
            {items.map((item, idx) => {
              const uid = item.is_history ? `h-${item.history_id}` : `a-${item.id}`;
              return (
                <button
                  key={uid}
                  onClick={() => canViewDetail && setSelectedItem({ id: item.id, isHistory: !!item.is_history })}
                  className="w-full text-left bg-white rounded-xl border shadow-sm p-3.5 active:bg-gray-50 transition-colors"
                >
                  {/* Line 1: Count + Full Name + Status */}
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[11px] font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      {onCustomerLocationClick ? (
                        <span
                          className="text-sm font-semibold text-blue-600 truncate"
                          onClick={(e) => { e.stopPropagation(); onCustomerLocationClick(item); }}
                        >
                          {item.customer_full_name || "—"}
                        </span>
                      ) : (
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {item.customer_full_name || "—"}
                        </p>
                      )}
                    </div>
                    {(() => {
                      const isTerminal = item.jo_status === "Completed" || item.jo_status === "Cancelled";
                      const canQuickAction = !item.is_history && !isTerminal && statusMenuActions.length > 0;
                      if (canQuickAction) {
                        return (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setStatusMenu({ jo: item, rect });
                            }}
                          >
                            <StatusBadge status={item.jo_status} jobOrder={item} />
                          </span>
                        );
                      }
                      return <StatusBadge status={item.is_history ? "Rescheduled" : item.jo_status} jobOrder={!item.is_history ? item : null} />;
                    })()}
                  </div>
                  {/* Line 2: GoFiber Status + Barangay */}
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-1">
                    {(() => {
                      const gf = item.gofiber_status;
                      const display = gf === "For Schedule of Installation" ? "FSOI" : (gf || "-");
                      if (gf === "Active") {
                        return <span className="bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded text-[11px]">{display}</span>;
                      }
                      return <span className="font-medium">{display}</span>;
                    })()}
                    {item.barangay && (
                      <span className="truncate flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {item.barangay}
                      </span>
                    )}
                    {(() => {
                      const isTerminal = item.jo_status === "Completed" || item.jo_status === "Cancelled";
                      const canQuickCS = !item.is_history && !isTerminal && canEditCallStatus;
                      const csLabel = item.call_status
                        ? (item.call_status === "Others" && item.call_status_other_text?.trim() ? `Other - ${item.call_status_other_text.trim()}` : item.call_status)
                        : "—";
                      const csBadge = item.call_status ? getCallStatusColor(item.call_status) : "bg-gray-100 text-gray-400";
                      return (
                        <span
                          className={`flex-shrink-0 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full max-w-[40%] truncate ${csBadge}${canQuickCS ? " cursor-pointer" : ""}`}
                          onClick={canQuickCS ? (e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setCsMenu({ jo: item, rect });
                          } : undefined}
                        >
                          {csLabel}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Line 3: Personnel + Agent + Chat */}
                  <div className="flex items-center gap-3 text-xs text-gray-600">
                    <span className="truncate"><span className="text-gray-400">Personnel:</span> {item.personnel_names || ""}</span>
                    {item.team_leader_name && (
                      <span className="truncate"><span className="text-gray-400">Agent:</span> {item.team_leader_name}</span>
                    )}
                    {canViewChat && !item.is_history && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setChatJobOrderId(item.id); }}
                        className={`ml-auto flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                          (item.chat_count || 0) === 0
                            ? "bg-gray-100 text-gray-400"
                            : item.has_unread_chat
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        {item.has_unread_chat ? (item.unread_count || 0) : (item.chat_count || 0)}
                      </button>
                    )}
                  </div>
                </button>
              );
            })}

            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div className="text-center py-4 text-gray-400 text-sm">Loading more…</div>
            )}
          </div>
        )}
      </div>

      {/* Quick status-action menu (mobile bottom sheet) */}
      {statusMenu && (
        <StatusActionMenu
          anchorRect={statusMenu.rect}
          onSelect={(action) => {
            setDirectAction({ type: action, jo: statusMenu.jo });
            setStatusMenu(null);
          }}
          onClose={() => setStatusMenu(null)}
          isMobile={true}
          visibleActions={statusMenuActions}
        />
      )}

      {/* Direct status modals from quick action */}
      {directAction?.type === "complete" && (
        <QuickCompleteDialog
          key={directAction.jo.id}
          jo={directAction.jo}
          onDone={() => { setDirectAction(null); handleRefresh(); }}
          onCancel={() => setDirectAction(null)}
        />
      )}
      {directAction?.type === "cancel" && (
        <QuickCancelDialog
          key={directAction.jo.id}
          jo={directAction.jo}
          onDone={() => { setDirectAction(null); handleRefresh(); }}
          onCancel={() => setDirectAction(null)}
        />
      )}
      {directAction?.type === "reschedule" && (
        <RescheduleModal
          key={directAction.jo.id}
          jobOrderId={directAction.jo.id}
          joNumber={directAction.jo.jo_number}
          currentJobDate={directAction.jo.job_date}
          ageingOriginDate={directAction.jo.ageing_origin_date}
          customerName={directAction.jo.customer_full_name}
          customerAddress={directAction.jo.complete_address}
          initialRemark={directAction.jo.jo_status_remarks || ""}
          initialTeamNumber={directAction.jo.team_number || ""}
          initialPersonnelNames={directAction.jo.personnel_names || ""}
          teamAssignedByCompany={directAction.jo.team_assigned_by_company}
          onClose={() => setDirectAction(null)}
          onComplete={() => { setDirectAction(null); handleRefresh(); }}
        />
      )}

      {/* Quick call-status action menu (mobile bottom sheet) */}
      {csMenu && (
        <CallStatusActionMenu
          options={NI_CALL_OPTIONS}
          anchorRect={csMenu.rect}
          onSelect={(status) => {
            setCsAction({ jo: csMenu.jo, selectedStatus: status });
            setCsMenu(null);
          }}
          onClose={() => setCsMenu(null)}
          isMobile={true}
        />
      )}

      {/* Quick call-status dialog */}
      {csAction && (
        <QuickCallStatusDialog
          key={csAction.jo.id}
          jo={csAction.jo}
          selectedStatus={csAction.selectedStatus}
          onDone={() => { setCsAction(null); handleRefresh(); }}
          onCancel={() => setCsAction(null)}
        />
      )}
    </div>
  );
}
